import { z } from 'zod'
import { Feature, FeatureCollection, MultiPolygon, Polygon, Position } from 'geojson'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import styled from 'styled-components'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import GeoJson from '~/components/googleMap/geoJson'
import GoogleMap from '~/components/googleMap/map'
import PolygonEditor, {
  DemarcationPoint,
  GhostDemarcationPoint,
  usePolygonEditor,
} from '~/components/googleMap/polygonEditor'
import { BaseActionDefinitionEnhanced, Type, fromUnknownError } from '~/prix'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import checkIntersects from '@turf/boolean-intersects'
import getDifference from '@turf/difference'
import getIntersect from '@turf/intersect'
import createBuffer from '@turf/buffer'
import { toastError, toastSuccess } from '~/components/toasts'
import Modal from '~/prix/react/components/modal'
import { omit } from '~/prix/utils/types/object'
import Marker from './googleMap/marker'
import { SmartMappingOptions } from '~/packages/geo/applySmartMapping/smartMapping.typing'
import {
  Layer,
  SpaceDelimitationGeoStandardWithShape,
} from '~/packages/general/variable/spaceTime.types'
import {
  faArrowRotateLeft,
  faArrowRotateRight,
  faCircleQuestion,
  faFillDrip,
  faFloppyDisk,
  faList,
  faMagnifyingGlass,
  faObjectUngroup,
  faPenToSquare,
  faPlus,
  faScissors,
  faTrash,
  faWandMagicSparkles,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { featureCollection as createFeatureCollection } from '@turf/helpers'
import { area, transformScale } from '@turf/turf'
import theme from '~/design'
import createBbox from '@turf/bbox'
import { OverlayLoading } from '~/components/mapContent/styles/overlayLoading'
import viewListDefinition from '~/packages/general/view/list/viewList.definition'
import { useVariableFetchList } from './utils/hooks/useVariableFetchList'
import { useInitialSpaceLayer } from './utils/hooks/useInitialSpaceLayer'
import { Delimitation, useDelimitation } from './utils/hooks/useDelimitation'
import { ButtonVariants } from './buttonVariants'
import { ScreenWrapper } from './screenWrapper'
import { ListInformationWrapper } from './listInformationWrapper'
import { MainMenuControls } from './mainMenuControls'
import { LatLngBoundsLiteral, LatLngLiteral } from './map/mapPosition'
import ContextMenuButton from '~/prix/react/components/contextMenu/contextMenuButton'
import ConfirmModal from './confirmModal'

const toolbarTooltipId = 'edit-coverage-area-toolbar-tooltip'

type ToolHelpTopic = {
  id: string
  title: string
  description: string
  shortcut?: string
  icon: IconDefinition
}

function positionsAreEqual(left: Position, right: Position): boolean {
  return left.length >= 2 && right.length >= 2 && left[0] === right[0] && left[1] === right[1]
}

function ensureClosedLinearRing(ring: Position[]): Position[] {
  if (ring.length === 0) {
    return ring
  }

  const first = ring[0]
  const last = ring[ring.length - 1]
  if (!positionsAreEqual(first, last)) {
    return [...ring, first]
  }

  return ring
}

function computeSignedAreaOfLinearRing(ring: Position[]): number {
  if (ring.length < 4) {
    return 0
  }

  const closedRing = ensureClosedLinearRing(ring)
  let signedArea = 0
  for (let index = 0; index < closedRing.length - 1; index++) {
    const current = closedRing[index]
    const next = closedRing[index + 1]
    signedArea += current[0] * next[1] - next[0] * current[1]
  }

  return signedArea / 2
}

function polygonOuterRingArea(polygon: Polygon): number {
  const outerRing = polygon.coordinates[0]
  if (!outerRing) {
    return 0
  }
  return Math.abs(computeSignedAreaOfLinearRing(outerRing))
}

function removePolygonHoles(polygon: Polygon): Polygon | null {
  const outerRing = polygon.coordinates[0]
  if (!outerRing) {
    return null
  }

  const closedOuterRing = ensureClosedLinearRing(outerRing)
  if (closedOuterRing.length < 4) {
    return null
  }

  return {
    type: 'Polygon',
    coordinates: [closedOuterRing],
  }
}

function getLargestPolygonFromMultiPolygon(multiPolygon: MultiPolygon): Polygon | null {
  let largestPolygon: Polygon | null = null
  let largestArea = 0

  for (const polygonCoordinates of multiPolygon.coordinates) {
    const candidatePolygon: Polygon = {
      type: 'Polygon',
      coordinates: polygonCoordinates,
    }

    const candidateArea = polygonOuterRingArea(candidatePolygon)
    if (candidateArea > largestArea) {
      largestArea = candidateArea
      largestPolygon = candidatePolygon
    }
  }

  return largestPolygon
}

function getLargestPolygonFromMultiPolygonPreservingHoles(
  multiPolygon: MultiPolygon,
): Polygon | null {
  let largestPolygonCoordinates: Position[][] | null = null
  let largestArea = 0

  for (const polygonCoordinates of multiPolygon.coordinates) {
    const candidatePolygon: Polygon = {
      type: 'Polygon',
      coordinates: polygonCoordinates,
    }

    const candidateArea = polygonOuterRingArea(candidatePolygon)
    if (candidateArea > largestArea) {
      largestArea = candidateArea
      largestPolygonCoordinates = polygonCoordinates
    }
  }

  if (!largestPolygonCoordinates) {
    return null
  }

  return {
    type: 'Polygon',
    coordinates: largestPolygonCoordinates,
  }
}

function simplifyCoverageAreaGeometry(geometry: Polygon | MultiPolygon): Polygon | null {
  if (geometry.type === 'Polygon') {
    return removePolygonHoles(geometry)
  }

  const largestPolygon = getLargestPolygonFromMultiPolygon(geometry)
  if (!largestPolygon) {
    return null
  }

  return removePolygonHoles(largestPolygon)
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) {
    return false
  }

  const tagName = target.tagName
  if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT') {
    return true
  }

  return target.isContentEditable
}

function normalizeTextForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .trim()
}

function getCoverageKindLabel(kind: string | null | undefined): string | null {
  if (kind === 'smartMapping') {
    return 'Inteligente'
  }
  if (kind === 'manualDrawing') {
    return 'Manual'
  }
  if (kind === 'authorityProvided') {
    return 'Oficial'
  }
  if (kind === 'openStreetMapSourced') {
    return 'OpenStreetMap'
  }
  if (kind === 'deprecated') {
    return 'Descontinuado'
  }

  return null
}

const Aside = styled.aside`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  max-height: 100%;

  background-color: ${({ theme }) => theme.colors.lightGrey};

  @media (max-width: 768px) {
    max-height: 80vh;
  }

  .header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${({ theme }) => theme.padding(1)};
    padding: ${({ theme }) => theme.padding(2)};
    background-color: ${({ theme }) => theme.colors.lightText};
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    .header-text {
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
      width: 100%;
    }

    .header-title {
      font-size: 14px;
      font-weight: 700;
      color: ${({ theme }) => theme.colors.primary};
      line-height: 1.2;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .header-subtitle {
      font-size: 12px;
      color: ${({ theme }) => theme.colors.darkGrey};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .badges {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: ${({ theme }) => theme.padding(1)};
      flex: 0 0 auto;
    }

    .badge {
      font-size: 12px;
      padding: 2px 8px;
      border-radius: 999px;
      background-color: ${({ theme }) => theme.colors.lightGrey};
      color: ${({ theme }) => theme.colors.darkText};
      border: 1px solid rgba(0, 0, 0, 0.08);
      user-select: none;
    }

    .badge-active {
      background-color: rgba(255, 105, 0, 0.12);
      border-color: rgba(255, 105, 0, 0.25);
      color: ${({ theme }) => theme.colors.secondary};
      font-weight: 700;
    }
  }

  .items {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: ${({ theme }) => theme.padding(1)};

    scrollbar-width: thin;
  }

  .search-area {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: ${({ theme }) => theme.padding(1)};
    background-color: ${({ theme }) => theme.colors.lightText};
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    margin-top: 10px;
  }

  .search-icon {
    position: absolute;
    left: 10px;
    color: ${({ theme }) => theme.colors.darkGrey};
    pointer-events: none;
  }

  .search-input {
    width: 100%;
    height: 36px;
    padding: 0 36px 0 32px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: ${({ theme }) => theme.borderRadius};
    background-color: ${({ theme }) => theme.colors.lightGrey};
    color: ${({ theme }) => theme.colors.darkText};
    font-size: 13px;
    outline: none;

    &:focus {
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: 0 0 0 2px rgba(0, 97, 175, 0.12);
    }
  }

  .search-clear-button {
    position: absolute;
    right: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: ${({ theme }) => theme.borderRadius};
    background: transparent;
    color: ${({ theme }) => theme.colors.darkGrey};
    cursor: pointer;

    &:hover {
      background-color: rgba(0, 0, 0, 0.06);
      color: ${({ theme }) => theme.colors.darkText};
    }
  }

  .search-count {
    color: ${({ theme }) => theme.colors.darkGrey};
    font-size: 12px;
    line-height: 1.25;
  }

  .empty-list {
    padding: ${({ theme }) => theme.padding(2)};
    color: ${({ theme }) => theme.colors.darkGrey};
    font-size: 13px;
    text-align: center;
  }

  ul {
    padding: 0;
    margin: 0;
    width: 100%;
    list-style-type: none;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.padding(1)};
  }

  li {
    text-decoration: none;
    line-height: 1.25;
    display: flex;
    flex-direction: column;
    border-radius: ${({ theme }) => theme.borderRadius};
    border: 1px solid rgba(0, 0, 0, 0.06);
    background-color: ${({ theme }) => theme.colors.lightText};
    overflow: hidden;
    transition: box-shadow ${({ theme }) => theme.transitionDurationMs}ms
      ${({ theme }) => theme.transitionTimingFunction};

    &:hover {
      box-shadow: ${({ theme }) => theme.shadow(1)};
    }

    &.is-active {
      border-color: ${({ theme }) => theme.colors.primary};
      background-color: ${({ theme }) => `${theme.colors.primary}1A`};
      box-shadow: ${({ theme }) => theme.shadow(1)};
    }
  }

  .item-card {
    display: flex;
    align-items: flex-start;
    gap: ${({ theme }) => theme.padding(1)};
    padding: ${({ theme }) => theme.padding(2)};
    width: 100%;

    border: none;
    background: transparent;
    cursor: pointer;
    text-align: left;
  }

  .item-dot {
    width: 10px;
    height: 10px;
    margin-top: 3px;
    border-radius: 999px;
    flex: 0 0 auto;
    background-color: ${({ theme }) => theme.colors.mediumGrey};
  }

  .item-dot[data-mapped='true'] {
    background-color: ${({ theme }) => theme.colors.tertiary};
  }

  .item-text {
    display: flex;
    flex: 1;
    min-width: 0;
    flex-direction: column;
    gap: 2px;
  }

  .item-header {
    display: flex;
    flex: 1;
    justify-content: space-between;
    align-items: center;
    gap: 5px;

    .tag {
      padding: 2px 8px;
      border-radius: ${({ theme }) => theme.borderRadius};
      border: 1px solid;
      font-size: 10px;
      font-weight: 400;
    }
  }

  .item-title {
    font-size: 14px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.darkText};
    line-height: 1.2;
  }

  .item-subtitle {
    font-size: 12px;
    color: ${({ theme }) => theme.colors.darkGrey};
  }

  .item-content-wrapper {
    display: flex;
    flex-direction: column;
    padding: ${({ theme }) => theme.padding(2)};
    background-color: rgba(236, 240, 241, 0.65);
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }
`

const FloatingToolbar = styled.div`
  position: absolute;
  top: 85px;
  left: ${({ theme }) => theme.padding(1)};
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border-radius: ${({ theme }) => theme.borderRadius};
  background-color: ${({ theme }) => theme.colors.lightText};
  box-shadow: ${({ theme }) => theme.shadow(1)};

  @supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
    background-color: ${({ theme }) => theme.backdropBackground};
    -webkit-backdrop-filter: ${({ theme }) => theme.backdropFilter};
    backdrop-filter: ${({ theme }) => theme.backdropFilter};
  }

  @media (max-width: 768px) {
    top: auto;
    bottom: calc(113px + ${({ theme }) => theme.padding(1)} + ${({ theme }) => theme.padding(1)});
    padding: 0;
    background: transparent;
  }
`

const ToolbarButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const ToolbarTooltipTarget = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
`

const ToolbarIconButton = styled(ButtonVariants)`
  width: 40px;
  min-width: 40px;
  height: 40px;
  padding: 0;
  justify-content: center;

  svg {
    margin-right: 0;
  }

  @media (max-width: 768px) {
    background-color: ${({ theme }) => theme.colors.lightText};

    svg {
      color: ${({ theme }) => theme.colors.darkText};
    }

    &.danger-button {
      background-color: ${({ theme }) => theme.colors.danger};

      svg {
        color: ${({ theme }) => theme.colors.lightText};
      }
    }
  }
`

const HelpModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: min(720px, 82vh);
  padding: 20px;
  gap: 16px;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.darkText};
`

const HelpModalHeader = styled.header`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-right: 32px;
`

const HelpModalTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 20px;
  font-weight: 700;
  line-height: 1.25;
`

const HelpModalSubtitle = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.darkGrey};
  font-size: 13px;
  line-height: 1.45;
`

const HelpTopicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 10px;
  min-height: 0;
  overflow-y: auto;
  padding-right: 6px;
  margin-right: -6px;
`

const HelpTopicCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  padding: 10px;
  border-radius: ${({ theme }) => theme.borderRadius};
  border: 1px solid rgba(0, 0, 0, 0.06);
  background-color: ${({ theme }) => theme.colors.lightGrey};
`

const HelpTopicHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const HelpTopicIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  color: ${({ theme }) => theme.colors.primary};
`

const HelpTopicTitle = styled.h3`
  min-width: 0;
  margin: 0;
  color: ${({ theme }) => theme.colors.darkText};
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
`

const HelpTopicDescription = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.darkGrey};
  font-size: 12px;
  line-height: 1.4;
`

const HelpShortcut = styled.kbd`
  align-self: flex-start;
  margin-top: auto;
  padding: 3px 7px;
  border-radius: ${({ theme }) => theme.borderRadius};
  border: 1px solid rgba(255, 105, 0, 0.22);
  background-color: rgba(255, 105, 0, 0.1);
  color: ${({ theme }) => theme.colors.secondary};
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.3;
`

const StatusPanel = styled.aside`
  position: absolute;
  z-index: 0;
  top: calc(40px + 0.6rem);
  right: ${({ theme }) => theme.padding(1)};
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 20vw;
  min-width: 335px;
  padding: ${({ theme }) => theme.padding(2)};
  border-radius: ${({ theme }) => theme.borderRadius};
  background-color: ${({ theme }) => theme.colors.lightText};
  box-shadow: ${({ theme }) => theme.shadow(1)};

  @supports ((-webkit-backdrop-filter: none) or (backdrop-filter: none)) {
    background-color: ${({ theme }) => theme.backdropBackground};
    -webkit-backdrop-filter: ${({ theme }) => theme.backdropFilter};
    backdrop-filter: ${({ theme }) => theme.backdropFilter};
  }

  @media (max-width: 768px) {
    display: none;
  }
`

const StatusTitle = styled.div`
  color: ${({ theme }) => theme.colors.darkText};
  font-size: 14px;
  font-weight: 700;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StatusSubtitle = styled.div`
  color: ${({ theme }) => theme.colors.darkGrey};
  font-size: 12px;
  line-height: 1.25;
`

const StatusBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.padding(1)};
  margin-top: ${({ theme }) => theme.padding(1)};
`

const StatusBadge = styled.span<{ $isActive?: boolean }>`
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid
    ${({ $isActive, theme }) => ($isActive ? 'rgba(255, 105, 0, 0.25)' : 'rgba(0, 0, 0, 0.08)')};
  background-color: ${({ $isActive, theme }) =>
    $isActive ? 'rgba(255, 105, 0, 0.12)' : theme.colors.lightGrey};
  color: ${({ $isActive, theme }) => ($isActive ? theme.colors.secondary : theme.colors.darkText)};
  font-size: 12px;
  font-weight: ${({ $isActive }) => ($isActive ? 700 : 400)};
`

const WrapperSmartMapping = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;

  label {
    margin-bottom: 1rem;
  }
`

const BottomBar = styled.div`
  padding: 10px 15px;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  border-top: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  background: #ffffff;
`

export const ItemContentWrapper = styled.div.attrs({
  className: 'item-content-wrapper',
})``

export interface BaseItem {
  key: string
  name: string
  coverageArea: MultiPolygon | Polygon | null
  kind?: string | null
  latitude?: number
  longitude?: number
}

export type Item = BaseItem

type ZSchema = z.ZodTypeAny

export interface EditCoverageAreaProps<
  Item extends BaseItem,
  FetchInSchema extends ZSchema = ZSchema,
  FetchOutSchema extends ZSchema = ZSchema,
  EditInSchema extends ZSchema = ZSchema,
  EditOutSchema extends ZSchema = ZSchema,
  SmartInSchema extends ZSchema = ZSchema,
  SmartOutSchema extends ZSchema = ZSchema,
> {
  recommendedSmartMappingOptions: SmartMappingOptions
  fetchDefinition: BaseActionDefinitionEnhanced<FetchInSchema, FetchOutSchema>
  editCoverageAreaDefinition: BaseActionDefinitionEnhanced<EditInSchema, EditOutSchema>
  smartMappingDefinition: BaseActionDefinitionEnhanced<SmartInSchema, SmartOutSchema>
  enablePointsEditing?: boolean
  visiblePolygons?: 'all' | 'neighbors' | 'intersected' | 'none'
  withTopology?: boolean
  compatibleDelimitationTypes?: string[]
  incompatibleDelimitationMessage?: string
  getSmartMappingBlockReason?: (delimitation: Delimitation | null) => string | null
  renderItemContent?: (item: Item, onChange: (item: Item) => void) => React.ReactNode
  getCustomProperty?: (feature: Feature<Polygon | MultiPolygon, any>) => Record<string, any>
  renderItemTag?: (item: Item) => React.ReactNode
}

export const EditCoverageArea = <
  Item extends BaseItem,
  FetchInSchema extends ZSchema,
  FetchOutSchema extends ZSchema,
  EditInSchema extends ZSchema,
  EditOutSchema extends ZSchema,
  SmartInSchema extends ZSchema,
  SmartOutSchema extends ZSchema,
>({
  recommendedSmartMappingOptions,
  fetchDefinition,
  editCoverageAreaDefinition,
  smartMappingDefinition,
  enablePointsEditing = true,
  visiblePolygons = 'all',
  withTopology = true,
  compatibleDelimitationTypes = ['municipality'],
  incompatibleDelimitationMessage = 'Selecione um município',
  getSmartMappingBlockReason,
  renderItemContent,
  getCustomProperty,
  renderItemTag,
}: EditCoverageAreaProps<
  Item,
  FetchInSchema,
  FetchOutSchema,
  EditInSchema,
  EditOutSchema,
  SmartInSchema,
  SmartOutSchema
>) => {
  type FeatureProperties = Omit<Item, 'coverageArea'>
  type MapPosition =
    | {
        bounds: LatLngBoundsLiteral
      }
    | {
        center: LatLngLiteral
        zoom: number
      }
  const [coveragesFeatureCollection, setCoveragesFeatureCollection] = useState<FeatureCollection<
    Polygon | null,
    FeatureProperties
  > | null>(null)
  // Estado: se o mapeamento inteligente está aberto
  const [isSmartMappingOpen, setIsSmartMappingOpen] = useState(false)
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false)
  const [itemSearchText, setItemSearchText] = useState('')
  const [smartMappingOptions, setSmartMappingOptions] = useState(recommendedSmartMappingOptions)

  // Estado: delimitação é compatível com a tela
  const [isCompatibleDelimitation, setIsCompatibleDelimitation] = useState<boolean>(true)

  // Estado: item selecionado
  const [selectedItem, setSelectedItem] = useState<Item | null>(null)

  // Estado: polígono selecionado
  const [selectedPolygonFeature, setSelectedPolygonFeature] = useState<Feature<
    Polygon,
    FeatureProperties
  > | null>(null)

  const [hasPendingPolygonEdits, setHasPendingPolygonEdits] = useState(false)
  const [hasUnsavedCoverageChanges, setHasUnsavedCoverageChanges] = useState(false)
  const [pendingBaseLayersConfirmation, setPendingBaseLayersConfirmation] = useState<
    Layer[] | null
  >(null)
  const [isClearAllPolygonsConfirmationOpen, setIsClearAllPolygonsConfirmationOpen] =
    useState(false)

  type EditorHistorySnapshot = {
    coveragesFeatureCollection: FeatureCollection<Polygon | null, FeatureProperties> | null
    selectedItem: Item | null
    selectedPolygonFeature: Feature<Polygon, FeatureProperties> | null
    polygonEditorPoints: Array<DemarcationPoint>
    hasPendingPolygonEdits: boolean
  }

  const maxHistorySize = 100
  const undoStackRef = useRef<EditorHistorySnapshot[]>([])
  const redoStackRef = useRef<EditorHistorySnapshot[]>([])
  const [, setHistoryVersion] = useState(0)
  const [restoreToken, setRestoreToken] = useState(0)
  const [mapNavigationPosition, setMapNavigationPosition] = useState<MapPosition | null>(null)

  const coveragesFeatureCollectionRef = useRef<FeatureCollection<
    Polygon | null,
    FeatureProperties
  > | null>(null)
  const selectedItemRef = useRef<Item | null>(null)
  const selectedPolygonFeatureRef = useRef<Feature<Polygon, FeatureProperties> | null>(null)
  const hasPendingPolygonEditsRef = useRef(false)
  const hasUnsavedCoverageChangesRef = useRef(false)
  const polygonEditorPointsRef = useRef<Array<DemarcationPoint>>([])
  const pendingRestoreSnapshotRef = useRef<EditorHistorySnapshot | null>(null)
  const delimitationSessionKeyRef = useRef<string | null>(null)

  coveragesFeatureCollectionRef.current = coveragesFeatureCollection
  selectedItemRef.current = selectedItem
  selectedPolygonFeatureRef.current = selectedPolygonFeature
  hasPendingPolygonEditsRef.current = hasPendingPolygonEdits
  hasUnsavedCoverageChangesRef.current = hasUnsavedCoverageChanges

  const markUnsavedCoverageChanges = useCallback(() => {
    if (hasUnsavedCoverageChangesRef.current) {
      return
    }
    hasUnsavedCoverageChangesRef.current = true
    setHasUnsavedCoverageChanges(true)
  }, [])

  const clearUnsavedCoverageChanges = useCallback(() => {
    if (!hasUnsavedCoverageChangesRef.current) {
      return
    }
    hasUnsavedCoverageChangesRef.current = false
    setHasUnsavedCoverageChanges(false)
  }, [])

  const getCurrentSnapshot = useCallback((): EditorHistorySnapshot => {
    return {
      coveragesFeatureCollection: coveragesFeatureCollectionRef.current,
      selectedItem: selectedItemRef.current,
      selectedPolygonFeature: selectedPolygonFeatureRef.current,
      polygonEditorPoints: polygonEditorPointsRef.current,
      hasPendingPolygonEdits: hasPendingPolygonEditsRef.current,
    }
  }, [])

  const pushUndoSnapshot = useCallback(() => {
    const snapshot = getCurrentSnapshot()
    const undoStack = undoStackRef.current
    const lastSnapshot = undoStack.length > 0 ? undoStack[undoStack.length - 1] : null
    if (
      lastSnapshot &&
      lastSnapshot.coveragesFeatureCollection === snapshot.coveragesFeatureCollection &&
      lastSnapshot.selectedItem === snapshot.selectedItem &&
      lastSnapshot.selectedPolygonFeature === snapshot.selectedPolygonFeature &&
      lastSnapshot.polygonEditorPoints === snapshot.polygonEditorPoints &&
      lastSnapshot.hasPendingPolygonEdits === snapshot.hasPendingPolygonEdits
    ) {
      return
    }

    undoStack.push(snapshot)
    if (undoStack.length > maxHistorySize) {
      undoStack.shift()
    }
    redoStackRef.current = []
    setHistoryVersion(version => version + 1)
  }, [getCurrentSnapshot])

  const restoreSnapshot = useCallback((snapshot: EditorHistorySnapshot) => {
    pendingRestoreSnapshotRef.current = snapshot
    setCoveragesFeatureCollection(snapshot.coveragesFeatureCollection)
    setSelectedItem(snapshot.selectedItem)
    setSelectedPolygonFeature(snapshot.selectedPolygonFeature)
    setHasPendingPolygonEdits(snapshot.hasPendingPolygonEdits)
    setRestoreToken(token => token + 1)
  }, [])

  const handleUndo = useCallback(() => {
    const undoStack = undoStackRef.current
    const previousSnapshot = undoStack.pop()
    if (!previousSnapshot) {
      return
    }

    const redoStack = redoStackRef.current
    redoStack.push(getCurrentSnapshot())
    if (redoStack.length > maxHistorySize) {
      redoStack.shift()
    }

    restoreSnapshot(previousSnapshot)
    setHistoryVersion(version => version + 1)
  }, [getCurrentSnapshot, restoreSnapshot])

  const handleRedo = useCallback(() => {
    const redoStack = redoStackRef.current
    const nextSnapshot = redoStack.pop()
    if (!nextSnapshot) {
      return
    }

    const undoStack = undoStackRef.current
    undoStack.push(getCurrentSnapshot())
    if (undoStack.length > maxHistorySize) {
      undoStack.shift()
    }

    restoreSnapshot(nextSnapshot)
    setHistoryVersion(version => version + 1)
  }, [getCurrentSnapshot, restoreSnapshot])

  // Seleção inicial de camadas base
  const [baseLayers, setBaseLayers] = useState<Layer[]>([])
  const baseLayersRef = useRef<Layer[]>([])
  baseLayersRef.current = baseLayers

  const isDelimitationChangedBetweenLayers = useCallback(
    (previousLayers: Layer[], nextLayers: Layer[]) => {
      const previousSpace = previousLayers[0]?.space as
        | (Layer['space'] & { delimitationKey?: string | null })
        | undefined
      const nextSpace = nextLayers[0]?.space as
        | (Layer['space'] & { delimitationKey?: string | null })
        | undefined

      if (!previousSpace || !nextSpace) {
        return false
      }

      const previousDelimitationId =
        'delimitationId' in previousSpace ? previousSpace.delimitationId : null
      const nextDelimitationId = 'delimitationId' in nextSpace ? nextSpace.delimitationId : null

      return (
        previousSpace.delimitationType !== nextSpace.delimitationType ||
        previousDelimitationId !== nextDelimitationId ||
        (previousSpace.delimitationKey ?? null) !== (nextSpace.delimitationKey ?? null)
      )
    },
    [],
  )

  const setBaseLayersWithUnsavedConfirmation = useCallback(
    (value: Layer[] | ((layers: Layer[]) => Layer[])) => {
      const previousLayers = baseLayersRef.current
      const nextLayers = typeof value === 'function' ? value(previousLayers) : value
      const changedDelimitation = isDelimitationChangedBetweenLayers(previousLayers, nextLayers)

      const hasUnsavedChanges =
        hasPendingPolygonEditsRef.current || hasUnsavedCoverageChangesRef.current
      if (changedDelimitation && hasUnsavedChanges) {
        setPendingBaseLayersConfirmation(nextLayers)
        return
      }

      baseLayersRef.current = nextLayers
      setBaseLayers(nextLayers)
      if (changedDelimitation) {
        setHasPendingPolygonEdits(false)
        clearUnsavedCoverageChanges()
      }
    },
    [clearUnsavedCoverageChanges, isDelimitationChangedBetweenLayers],
  )

  const handleConfirmBaseLayersChange = useCallback(() => {
    if (!pendingBaseLayersConfirmation) {
      return
    }

    setBaseLayers(pendingBaseLayersConfirmation)
    baseLayersRef.current = pendingBaseLayersConfirmation
    setHasPendingPolygonEdits(false)
    clearUnsavedCoverageChanges()
    setPendingBaseLayersConfirmation(null)
  }, [clearUnsavedCoverageChanges, pendingBaseLayersConfirmation])

  const layers: Layer[] = useMemo(() => {
    return baseLayers
  }, [baseLayers])

  // Hook para obter a lista de variáveis
  const { variables, currentTool, variablesAreLoading } = useVariableFetchList(viewListDefinition)

  // Hook para obter shape inicial da localidade
  const { initialSpaceLayer, isLoadingInitialSpace } = useInitialSpaceLayer()

  // Hook para navegar por posição de mapa
  const { delimitation, setDelimitation, currentResult, mapPosition } = useDelimitation(
    fetchDefinition,
    setCoveragesFeatureCollection,
    setIsCompatibleDelimitation,
    compatibleDelimitationTypes,
  )

  useEffect(() => {
    if (!currentResult.result) {
      return
    }
    clearUnsavedCoverageChanges()
    setHasPendingPolygonEdits(false)
  }, [clearUnsavedCoverageChanges, currentResult.result])

  useEffect(() => {
    const sessionKey = delimitation
      ? `${delimitation.type}:${delimitation.id}:${delimitation.key ?? ''}`
      : null
    if (sessionKey === delimitationSessionKeyRef.current) {
      return
    }

    delimitationSessionKeyRef.current = sessionKey
    undoStackRef.current = []
    redoStackRef.current = []
    setHistoryVersion(version => version + 1)
    setHasPendingPolygonEdits(false)
    clearUnsavedCoverageChanges()
    setSelectedItem(null)
    setSelectedPolygonFeature(null)
    setItemSearchText('')
    setMapNavigationPosition(mapPosition ?? null)
  }, [
    clearUnsavedCoverageChanges,
    delimitation?.id,
    delimitation?.key,
    delimitation?.type,
    mapPosition,
  ])

  const mapNavigationForItem = useCallback(
    (
      item: FeatureProperties & {
        coverageArea: Polygon | MultiPolygon | null
      },
    ): MapPosition | null => {
      if (item.coverageArea) {
        try {
          const polygonResized = transformScale(item.coverageArea, 1.3)
          const bbox = createBbox(polygonResized)
          const bounds: LatLngBoundsLiteral = {
            west: bbox[0],
            south: bbox[1],
            east: bbox[2],
            north: bbox[3],
          }
          return { bounds }
        } catch {
          // Fallback to point when geometry is malformed.
        }
      }

      if (typeof item.latitude === 'number' && typeof item.longitude === 'number') {
        return {
          center: { lat: item.latitude, lng: item.longitude },
          zoom: 16,
        }
      }

      return null
    },
    [],
  )

  const delimitationFeatureCollection = useMemo(() => {
    if (!delimitation) {
      return null
    }
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {
            key: 'delimitation',
            name: delimitation.name,
          },
          geometry: delimitation.shape,
        },
      ],
    } as FeatureCollection<Polygon | MultiPolygon, { key: string; name: string }>
  }, [delimitation])

  useEffect(() => {
    const space = layers[0]?.space
    if (!space) return
    // Se não houver shape, define a delimitação inicial
    if (!(space as SpaceDelimitationGeoStandardWithShape).shape) {
      setDelimitation(initialSpaceLayer)
    } else {
      setDelimitation({
        id: (space as SpaceDelimitationGeoStandardWithShape).delimitationId,
        key: (space as { delimitationKey?: string | null }).delimitationKey ?? null,
        name: (space as SpaceDelimitationGeoStandardWithShape).delimitationTitle ?? '',
        type: (space as SpaceDelimitationGeoStandardWithShape).delimitationType as string,
        shape: (space as SpaceDelimitationGeoStandardWithShape).shape as Polygon | MultiPolygon,
      } as Delimitation)
    }
  }, [initialSpaceLayer, layers[0]?.space])

  // Memoização das coberturas / polígonos filtrados
  const filteredCoveragesFeatureCollection = useMemo(() => {
    if (!coveragesFeatureCollection) {
      return null
    }

    let features = coveragesFeatureCollection.features.filter(
      feature => feature.properties.key !== selectedItem?.key,
    )

    let currentPolygonFeatureSelected = coveragesFeatureCollection.features.find(
      feature => feature.properties.key === selectedItem?.key,
    ) as Feature<Polygon> | null

    if (visiblePolygons === 'neighbors' || visiblePolygons === 'intersected') {
      if (visiblePolygons === 'neighbors') {
        currentPolygonFeatureSelected =
          currentPolygonFeatureSelected && currentPolygonFeatureSelected.geometry
            ? (createBuffer(currentPolygonFeatureSelected, 0.01) as Feature<Polygon> | null)
            : null
      }

      features = features.filter(
        feature =>
          currentPolygonFeatureSelected &&
          currentPolygonFeatureSelected.geometry &&
          feature.geometry &&
          checkIntersects(
            currentPolygonFeatureSelected as Feature<Polygon>,
            feature as Feature<Polygon>,
          ) &&
          getIntersect(
            createFeatureCollection([feature as Feature<Polygon>, currentPolygonFeatureSelected]),
          ),
      )
    } else if (visiblePolygons === 'none') {
      features = []
    }

    return {
      type: 'FeatureCollection',
      features: features.map(feature => ({
        ...feature,
        properties: {
          ...feature.properties,
          ...(getCustomProperty
            ? getCustomProperty(feature as Feature<Polygon | MultiPolygon, Item>)
            : {}),
        },
      })),
    } as FeatureCollection<Polygon | null, FeatureProperties>
  }, [selectedItem, coveragesFeatureCollection, visiblePolygons])

  // Aplicação do polígono selecionado
  const polygonEditor = usePolygonEditor(selectedPolygonFeature)
  polygonEditorPointsRef.current = polygonEditor.points

  useEffect(() => {
    setSelectedPolygonFeature(polygonEditor.polygon as Feature<Polygon, FeatureProperties> | null)
  }, [polygonEditor.polygon])

  useEffect(() => {
    const snapshotToRestore = pendingRestoreSnapshotRef.current
    if (!snapshotToRestore) {
      return
    }

    pendingRestoreSnapshotRef.current = null
    polygonEditor.setActiveGhostPoint(null)
    polygonEditor.setPoints(snapshotToRestore.polygonEditorPoints)
    polygonEditor.setIsDirty(snapshotToRestore.hasPendingPolygonEdits)
  }, [
    restoreToken,
    polygonEditor.setActiveGhostPoint,
    polygonEditor.setIsDirty,
    polygonEditor.setPoints,
  ])

  const applyPolygon = useCallback(() => {
    if (!selectedItem || !filteredCoveragesFeatureCollection || !polygonEditor.polygon) {
      return coveragesFeatureCollection
    }

    const newCoverageArea: Feature<Polygon, FeatureProperties> = {
      type: 'Feature',
      properties: {
        ...omit(selectedItem, ['coverageArea']),
        kind: 'manualDrawing',
      },
      geometry: polygonEditor.polygon.geometry,
    }

    if (!withTopology) {
      const newCoveragesFeatureCollection: FeatureCollection<Polygon | null, FeatureProperties> = {
        type: 'FeatureCollection',
        features: [
          ...filteredCoveragesFeatureCollection.features.filter(
            feature => feature.properties.key !== selectedItem.key,
          ),
          newCoverageArea,
        ],
      }
      setCoveragesFeatureCollection(newCoveragesFeatureCollection)
      markUnsavedCoverageChanges()

      return newCoveragesFeatureCollection
    }

    const intersectedFeatures = filteredCoveragesFeatureCollection.features.filter(
      feature =>
        feature.geometry &&
        checkIntersects(newCoverageArea, feature) &&
        getIntersect(createFeatureCollection([feature as Feature<Polygon>, newCoverageArea])),
    ) as Array<Feature<Polygon>>
    const notIntersectedFeatures = filteredCoveragesFeatureCollection.features.filter(
      feature => intersectedFeatures.indexOf(feature as Feature<Polygon>) === -1,
    )
    const intersectedFeaturesUpdated = intersectedFeatures.map(feature => {
      const subtracted = getDifference(createFeatureCollection([feature, newCoverageArea]))
      return {
        type: 'Feature',
        properties: {
          ...feature.properties,
          kind: 'manualDrawing',
        },
        geometry: subtracted?.geometry ?? null,
      } as Feature<Polygon, FeatureProperties>
    })

    // Order by name
    const newFeatures = [
      ...notIntersectedFeatures,
      newCoverageArea,
      ...intersectedFeaturesUpdated,
    ].sort((a, b) => (a.properties?.name > b.properties?.name ? 1 : -1))
    const newCoveragesFeatureCollection = {
      type: 'FeatureCollection',
      features: newFeatures,
    } as FeatureCollection<Polygon | null, FeatureProperties>

    setCoveragesFeatureCollection(newCoveragesFeatureCollection)
    markUnsavedCoverageChanges()
    return newCoveragesFeatureCollection
  }, [
    selectedItem,
    filteredCoveragesFeatureCollection,
    polygonEditor,
    withTopology,
    markUnsavedCoverageChanges,
  ])

  const clipFeatureCollectionByDelimitation = useCallback(
    (baseFeatureCollection: FeatureCollection<Polygon | null, FeatureProperties>) => {
      if (!delimitation?.shape) {
        return baseFeatureCollection
      }

      const delimitationFeature: Feature<Polygon | MultiPolygon, Record<string, never>> = {
        type: 'Feature',
        properties: {},
        geometry: delimitation.shape,
      }

      const updatedFeatures = baseFeatureCollection.features.map(feature => {
        const geometry = feature.geometry
        if (!geometry) {
          return feature
        }

        try {
          const intersection = getIntersect(
            createFeatureCollection([
              feature as unknown as Feature<Polygon | MultiPolygon>,
              delimitationFeature,
            ]),
          ) as unknown as Feature<Polygon | MultiPolygon> | null

          return {
            ...feature,
            geometry: intersection?.geometry ?? null,
          }
        } catch (error) {
          console.error(error)
          return {
            ...feature,
            geometry: null,
          }
        }
      })

      return {
        type: 'FeatureCollection',
        features: updatedFeatures,
      } as FeatureCollection<Polygon | null, FeatureProperties>
    },
    [delimitation?.shape],
  )

  const appendPolygonEditorPoint = useCallback(
    (position: google.maps.LatLngLiteral) => {
      if (!selectedItemRef.current) {
        return
      }

      pushUndoSnapshot()
      setHasPendingPolygonEdits(true)
      polygonEditor.appendPoint(position)
    },
    [polygonEditor.appendPoint, pushUndoSnapshot],
  )

  const handlePolygonEditorMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      const { latLng } = event
      if (!latLng) {
        return
      }

      appendPolygonEditorPoint(latLng.toJSON())
    },
    [appendPolygonEditorPoint],
  )

  const handlePolygonEditorMarkerDragEnd = useCallback(
    (point: DemarcationPoint, index: number) => {
      const handler = polygonEditor.onMarkerDragEnd(point, index)
      return (event: google.maps.MapMouseEvent) => {
        if (!selectedItemRef.current) {
          return
        }

        pushUndoSnapshot()
        setHasPendingPolygonEdits(true)
        handler(event)
      }
    },
    [polygonEditor.onMarkerDragEnd, pushUndoSnapshot],
  )

  const handlePolygonEditorGhostMarkerDragEnd = useCallback(
    (point: GhostDemarcationPoint) => {
      const handler = polygonEditor.onGhostMarkerDragEnd(point)
      return (event: google.maps.MapMouseEvent) => {
        if (!selectedItemRef.current) {
          return
        }

        pushUndoSnapshot()
        setHasPendingPolygonEdits(true)
        handler(event)
      }
    },
    [polygonEditor.onGhostMarkerDragEnd, pushUndoSnapshot],
  )

  const handlePolygonEditorMarkerClick = useCallback(
    (point: DemarcationPoint, index: number) => {
      const handler = polygonEditor.onMarkerClick(point, index)
      return (event: google.maps.MapMouseEvent) => {
        if (!selectedItemRef.current) {
          return
        }

        pushUndoSnapshot()
        setHasPendingPolygonEdits(true)
        handler(event)
      }
    },
    [polygonEditor.onMarkerClick, pushUndoSnapshot],
  )

  // Seleção de polígono ao selecionar item na lista
  useEffect(() => {
    if (!selectedItem) {
      setSelectedPolygonFeature(null)
      return
    }

    if (hasPendingPolygonEdits) {
      return
    }

    setSelectedPolygonFeature(
      (coveragesFeatureCollection?.features.find(
        feature => feature.geometry && feature.properties!.key === selectedItem?.key,
      ) ?? null) as Feature<Polygon, FeatureProperties> | null,
    )
  }, [selectedItem, coveragesFeatureCollection, hasPendingPolygonEdits])

  // Memoização dos pontos dos marcadores
  const handleClickMarker = useCallback(
    (point: Item | null) => {
      if (hasPendingPolygonEditsRef.current) {
        pushUndoSnapshot()
        applyPolygon()
        setHasPendingPolygonEdits(false)
      }

      if (point) {
        setSelectedItem(point as unknown as Item)
      } else {
        setSelectedItem(null)
      }
    },
    [applyPolygon, pushUndoSnapshot],
  )

  const handleDragEndSelectedMarker = useCallback(
    (_event: google.maps.MapMouseEvent, marker: google.maps.Marker) => {
      if (!selectedItem) {
        return
      }

      const position = marker.getPosition()
      if (!position) {
        return
      }

      pushUndoSnapshot()

      const latitude = position.lat()
      const longitude = position.lng()
      const selectedKey = selectedItem.key

      setSelectedItem(item => (item ? ({ ...item, latitude, longitude } as Item) : item))
      setCoveragesFeatureCollection(featureCollection => {
        if (!featureCollection) {
          return featureCollection
        }

        const updatedFeatures = featureCollection.features.map(feature => {
          if (feature.properties.key !== selectedKey) {
            return feature
          }

          return {
            ...feature,
            properties: {
              ...feature.properties,
              latitude,
              longitude,
            },
          }
        })

        return {
          ...featureCollection,
          features: updatedFeatures as Feature<Polygon | null, FeatureProperties>[],
        }
      })
      markUnsavedCoverageChanges()
    },
    [pushUndoSnapshot, selectedItem, markUnsavedCoverageChanges],
  )

  const points = useMemo(() => {
    if (!coveragesFeatureCollection || !enablePointsEditing) {
      return []
    }
    const coveragesFeatureCollectionPoints = coveragesFeatureCollection.features
      .filter(
        feature =>
          typeof feature.properties.latitude === 'number' &&
          typeof feature.properties.longitude === 'number',
      )
      .map(item => ({
        latitude: item?.properties?.latitude!,
        longitude: item?.properties?.longitude!,
        key: item?.properties?.key,
        name: item?.properties?.name,
        kind: item?.properties?.kind,
        coverageArea: item?.geometry as Polygon | MultiPolygon | null,
      }))
    if (!selectedItem) {
      return coveragesFeatureCollectionPoints.map((point, index) => (
        <Marker
          key={`marker-${point.key}`}
          position={{ lat: point.latitude, lng: point.longitude }}
          onClick={() => handleClickMarker(point as unknown as Item)}
        />
      ))
    }
    if (selectedItem && selectedItem.latitude && selectedItem.longitude) {
      return (
        <Marker
          position={{ lat: selectedItem.latitude, lng: selectedItem.longitude }}
          onClick={() => handleClickMarker(null)}
          onDragEnd={handleDragEndSelectedMarker}
        />
      )
    }
  }, [
    coveragesFeatureCollection,
    enablePointsEditing,
    handleClickMarker,
    handleDragEndSelectedMarker,
    selectedItem,
  ])

  const handlePolygonClick = useCallback(
    (event: google.maps.Data.MouseEvent) => {
      const activeItem = selectedItemRef.current
      const shouldDrawOnPolygonClick =
        Boolean(activeItem) &&
        (hasPendingPolygonEditsRef.current ||
          (activeItem?.coverageArea == null && polygonEditorPointsRef.current.length === 0))

      // While editing (or when starting a new polygon from scratch), clicks on other polygons
      // should behave as drawing interactions instead of selecting a different item.
      if (shouldDrawOnPolygonClick) {
        const { latLng } = event
        if (!latLng) {
          return
        }
        appendPolygonEditorPoint(latLng.toJSON())
        return
      }

      if (hasPendingPolygonEditsRef.current) {
        pushUndoSnapshot()
        applyPolygon()
        setHasPendingPolygonEdits(false)
      }

      const clickedKey = event.feature.getProperty('key') as string
      const feature = (filteredCoveragesFeatureCollection?.features.find(
        feature => feature.properties.key === clickedKey,
      ) ?? null) as Feature<Polygon, FeatureProperties> | null

      if (!feature) {
        return
      }

      setSelectedItem({
        ...feature.properties,
        coverageArea: feature.geometry,
      } as Item)

      requestAnimationFrame(() => {
        const element = document.getElementById(`li-${clickedKey}`)
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    },
    [appendPolygonEditorPoint, applyPolygon, filteredCoveragesFeatureCollection, pushUndoSnapshot],
  )

  // Troca de item na lista
  const handleChangeItem = useCallback(
    (item: Item) => {
      setSelectedItem(item)

      if (!coveragesFeatureCollection) {
        return
      }

      const newFeatures = coveragesFeatureCollection?.features.map(feature => {
        if (feature.properties.key === item.key) {
          return {
            ...feature,
            properties: omit(item, ['coverageArea']),
            geometry: item.coverageArea,
          }
        }
        return feature
      })
      setCoveragesFeatureCollection({
        type: 'FeatureCollection',
        features: newFeatures as Feature<Polygon, FeatureProperties>[],
      })
      markUnsavedCoverageChanges()
    },
    [coveragesFeatureCollection, markUnsavedCoverageChanges],
  )

  // Action para salvar e aplicar o polígono
  const save = useActionFunction(editCoverageAreaDefinition, {
    initialIsLoading: false,
  })

  const handleSave = useCallback(async () => {
    if (!delimitation) {
      return
    }

    try {
      if (hasPendingPolygonEditsRef.current) {
        pushUndoSnapshot()
      }
      const newFeatureCollection = applyPolygon()
      const clippedFeatureCollection = newFeatureCollection
        ? clipFeatureCollectionByDelimitation(newFeatureCollection)
        : newFeatureCollection
      setHasPendingPolygonEdits(false)

      await save.callAction({
        featureCollection: clippedFeatureCollection,
        delimitationPolygon: delimitation.shape,
        delimitationId: delimitation.id,
        delimitationKey: delimitation.key,
        delimitationType: delimitation.type,
      }).resultPromise
      setCoveragesFeatureCollection(clippedFeatureCollection)
      setSelectedItem(null)
      clearUnsavedCoverageChanges()
      toastSuccess('Atualizado com sucesso')
    } catch (error) {
      console.error(error)
      const finalError = fromUnknownError(error as Error)
      toastError(finalError.message)
    }
  }, [
    applyPolygon,
    clearUnsavedCoverageChanges,
    clipFeatureCollectionByDelimitation,
    delimitation,
    pushUndoSnapshot,
  ])

  // Action para mapeamento inteligente
  const smartMapping = useActionFunction(smartMappingDefinition, {
    initialIsLoading: false,
  })

  const handleRemoveFragments = useCallback(() => {
    if (delimitation?.shape.type === 'MultiPolygon' && delimitation.shape.coordinates.length > 1) {
      return
    }

    pushUndoSnapshot()

    const baseFeatureCollection = hasPendingPolygonEditsRef.current
      ? (applyPolygon() ?? coveragesFeatureCollectionRef.current)
      : coveragesFeatureCollectionRef.current
    setHasPendingPolygonEdits(false)
    if (!baseFeatureCollection) {
      return
    }

    const updatedFeatures = baseFeatureCollection.features.map(feature => {
      const geometry = feature.geometry
      if (!geometry) {
        return feature
      }

      if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
        const simplifiedGeometry = simplifyCoverageAreaGeometry(geometry)
        return {
          ...feature,
          geometry: simplifiedGeometry,
        }
      }

      return feature
    })

    setCoveragesFeatureCollection({
      type: 'FeatureCollection',
      features: updatedFeatures,
    } as FeatureCollection<Polygon | null, FeatureProperties>)
    markUnsavedCoverageChanges()

    setSelectedItem(currentSelectedItem => {
      if (!currentSelectedItem) {
        return currentSelectedItem
      }
      const updatedFeature = updatedFeatures.find(
        feature => feature.properties.key === currentSelectedItem.key,
      )
      if (!updatedFeature) {
        return currentSelectedItem
      }
      return {
        ...currentSelectedItem,
        coverageArea: updatedFeature.geometry ?? null,
      } as Item
    })

    toastSuccess('Fragmentos removidos com sucesso')
  }, [applyPolygon, delimitation?.shape, pushUndoSnapshot, markUnsavedCoverageChanges])

  const handleResolvePolygons = useCallback(() => {
    if (!selectedItem || !polygonEditor.polygon) {
      return
    }

    if (!polygonEditor.polygonIsValid.status) {
      toastError(polygonEditor.polygonIsValid.errorMessage || 'Polígono inválido')
      return
    }

    pushUndoSnapshot()
    applyPolygon()
    setHasPendingPolygonEdits(false)
    setSelectedItem(null)
    setSelectedPolygonFeature(null)
    markUnsavedCoverageChanges()
    toastSuccess('Polígonos resolvidos com sucesso')
  }, [
    applyPolygon,
    markUnsavedCoverageChanges,
    polygonEditor.polygon,
    polygonEditor.polygonIsValid,
    pushUndoSnapshot,
    selectedItem,
  ])

  const handleFillEntireDelimitation = useCallback(() => {
    if (!delimitation?.shape) {
      return
    }

    const currentSelectedItem = selectedItemRef.current
    if (!currentSelectedItem) {
      return
    }

    const baseFeatureCollection = coveragesFeatureCollectionRef.current
    if (!baseFeatureCollection) {
      return
    }

    const delimitationFeature: Feature<Polygon | MultiPolygon, Record<string, never>> = {
      type: 'Feature',
      properties: {},
      geometry: delimitation.shape,
    }

    let remainingAreaFeature: Feature<Polygon | MultiPolygon, Record<string, never>> | null =
      delimitationFeature

    for (const feature of baseFeatureCollection.features) {
      if (!remainingAreaFeature) {
        break
      }

      if (feature.properties.key === currentSelectedItem.key) {
        continue
      }

      const geometry = feature.geometry
      if (!geometry || (geometry.type !== 'Polygon' && geometry.type !== 'MultiPolygon')) {
        continue
      }

      const otherFeature: Feature<Polygon | MultiPolygon, Record<string, never>> = {
        type: 'Feature',
        properties: {},
        geometry,
      }

      const hasIntersection =
        checkIntersects(remainingAreaFeature, otherFeature) &&
        Boolean(getIntersect(createFeatureCollection([remainingAreaFeature, otherFeature])))

      if (!hasIntersection) {
        continue
      }

      try {
        const nextRemainingArea = getDifference(
          createFeatureCollection([remainingAreaFeature, otherFeature]),
        ) as unknown as Feature<Polygon | MultiPolygon> | null

        remainingAreaFeature = nextRemainingArea
          ? ({
              type: 'Feature',
              properties: {},
              geometry: nextRemainingArea.geometry,
            } as Feature<Polygon | MultiPolygon, Record<string, never>>)
          : null
      } catch (error) {
        console.error(error)
        toastError('Não foi possível preencher a área. Tente resolver os polígonos antes.')
        return
      }
    }

    const remainingGeometry = remainingAreaFeature?.geometry ?? null

    let finalPolygon: Polygon | null = null
    if (remainingGeometry?.type === 'Polygon') {
      finalPolygon = remainingGeometry
    } else if (remainingGeometry?.type === 'MultiPolygon') {
      finalPolygon = getLargestPolygonFromMultiPolygonPreservingHoles(remainingGeometry)
    }

    pushUndoSnapshot()
    setHasPendingPolygonEdits(false)
    polygonEditor.setActiveGhostPoint(null)
    polygonEditor.setIsDirty(false)

    const updatedFeatures = baseFeatureCollection.features.map(feature => {
      if (feature.properties.key !== currentSelectedItem.key) {
        return feature
      }

      return {
        ...feature,
        properties: {
          ...feature.properties,
          kind: 'manualDrawing',
        },
        geometry: finalPolygon,
      }
    })

    setCoveragesFeatureCollection({
      type: 'FeatureCollection',
      features: updatedFeatures,
    } as FeatureCollection<Polygon | null, FeatureProperties>)
    markUnsavedCoverageChanges()

    setSelectedItem({
      ...currentSelectedItem,
      kind: 'manualDrawing',
      coverageArea: finalPolygon,
    } as Item)

    toastSuccess('Área preenchida com sucesso')
  }, [
    delimitation?.shape,
    markUnsavedCoverageChanges,
    polygonEditor.setActiveGhostPoint,
    polygonEditor.setIsDirty,
    pushUndoSnapshot,
  ])

  const handleDeleteSelectedPolygon = useCallback(() => {
    const currentSelectedItem = selectedItemRef.current
    const baseFeatureCollection = coveragesFeatureCollectionRef.current
    if (!currentSelectedItem || !baseFeatureCollection) {
      return
    }

    pushUndoSnapshot()
    setHasPendingPolygonEdits(false)
    polygonEditor.setActiveGhostPoint(null)
    polygonEditor.setPoints([])
    polygonEditor.setIsDirty(false)
    setSelectedPolygonFeature(null)

    const updatedFeatures = baseFeatureCollection.features.map(feature => {
      if (feature.properties.key !== currentSelectedItem.key) {
        return feature
      }

      return {
        ...feature,
        properties: {
          ...feature.properties,
          kind: null,
        },
        geometry: null,
      }
    })

    setCoveragesFeatureCollection({
      type: 'FeatureCollection',
      features: updatedFeatures,
    } as FeatureCollection<Polygon | null, FeatureProperties>)
    markUnsavedCoverageChanges()

    setSelectedItem({
      ...currentSelectedItem,
      kind: null,
      coverageArea: null,
    } as Item)

    toastSuccess('Polígono selecionado apagado com sucesso')
  }, [
    markUnsavedCoverageChanges,
    polygonEditor.setActiveGhostPoint,
    polygonEditor.setIsDirty,
    polygonEditor.setPoints,
    pushUndoSnapshot,
  ])

  const clearAllPolygons = useCallback(() => {
    const baseFeatureCollection = coveragesFeatureCollectionRef.current
    if (!baseFeatureCollection) {
      return
    }

    pushUndoSnapshot()
    setHasPendingPolygonEdits(false)
    polygonEditor.setActiveGhostPoint(null)
    polygonEditor.setPoints([])
    polygonEditor.setIsDirty(false)
    setSelectedPolygonFeature(null)

    const updatedFeatures = baseFeatureCollection.features.map(feature => ({
      ...feature,
      properties: {
        ...feature.properties,
        kind: null,
      },
      geometry: null,
    }))

    setCoveragesFeatureCollection({
      type: 'FeatureCollection',
      features: updatedFeatures,
    } as FeatureCollection<Polygon | null, FeatureProperties>)
    markUnsavedCoverageChanges()

    setSelectedItem(currentSelectedItem => {
      if (!currentSelectedItem) {
        return currentSelectedItem
      }

      return {
        ...currentSelectedItem,
        kind: null,
        coverageArea: null,
      } as Item
    })

    toastSuccess('Todos os polígonos foram removidos com sucesso')
    setIsClearAllPolygonsConfirmationOpen(false)
  }, [
    markUnsavedCoverageChanges,
    polygonEditor.setActiveGhostPoint,
    polygonEditor.setIsDirty,
    polygonEditor.setPoints,
    pushUndoSnapshot,
  ])

  const handleClearAllPolygons = useCallback(() => {
    const baseFeatureCollection = coveragesFeatureCollectionRef.current
    if (!baseFeatureCollection) {
      return
    }

    setIsClearAllPolygonsConfirmationOpen(true)
  }, [])

  // Atualização de polígonos usando mapeamento inteligente
  const handleUpdatePolygonsUsingSmartMapping = useCallback(async () => {
    if (!delimitation?.shape) {
      return
    }

    setIsSmartMappingOpen(false)

    const smartMappingBlockReason = getSmartMappingBlockReason?.(delimitation) ?? null
    if (smartMappingBlockReason) {
      toastError(smartMappingBlockReason)
      return
    }

    try {
      const response = await smartMapping.callAction({
        ...smartMappingOptions,
        delimitationPolygon: delimitation.shape,
        delimitationId: delimitation.id,
        delimitationKey: delimitation.key,
        delimitationType: delimitation.type,
      }).resultPromise
      const newFeatureCollectionUpdated = response.featureCollection
      pushUndoSnapshot()
      setCoveragesFeatureCollection(newFeatureCollectionUpdated)
      setHasPendingPolygonEdits(false)
      markUnsavedCoverageChanges()
      toastSuccess('Mapeamento inteligente realizado com sucesso, pronto para editar e salvar')
      setIsSmartMappingOpen(false)
    } catch (error) {
      console.error(error)
      const finalError = fromUnknownError(error as Error)
      toastError(finalError.message)
    }
  }, [
    delimitation,
    getSmartMappingBlockReason,
    markUnsavedCoverageChanges,
    pushUndoSnapshot,
    smartMappingOptions,
  ])

  const currentMapPosition = useMemo(() => {
    return mapNavigationPosition ?? mapPosition
  }, [mapNavigationPosition, mapPosition])

  const isLoading = variablesAreLoading || isLoadingInitialSpace
  const isLoadingFeature = currentResult.isLoading || save.isLoading || smartMapping.isLoading
  const smartMappingBlockReason = getSmartMappingBlockReason?.(delimitation) ?? null
  const canUndo = undoStackRef.current.length > 0
  const canRedo = redoStackRef.current.length > 0
  const delimitationHasMultiplePolygons = Boolean(
    delimitation?.shape &&
    delimitation.shape.type === 'MultiPolygon' &&
    delimitation.shape.coordinates.length > 1,
  )
  const hasSelectedPolygon = Boolean(
    selectedItem &&
    (selectedItem.coverageArea ||
      selectedPolygonFeature?.geometry ||
      (hasPendingPolygonEdits && polygonEditor.points.length > 0)),
  )
  const hasAnyPolygon = Boolean(
    coveragesFeatureCollection?.features.some(feature => Boolean(feature.geometry)) ||
    (hasPendingPolygonEdits && polygonEditor.points.length > 0),
  )
  const mappedCoverageCount =
    coveragesFeatureCollection?.features.filter(feature => Boolean(feature.geometry)).length ?? 0
  const totalCoverageCount = coveragesFeatureCollection?.features.length ?? 0
  const normalizedItemSearchText = useMemo(
    () => normalizeTextForSearch(itemSearchText),
    [itemSearchText],
  )
  const filteredCoverageFeatures = useMemo(() => {
    if (!coveragesFeatureCollection) {
      return []
    }

    if (!normalizedItemSearchText) {
      return coveragesFeatureCollection.features
    }

    return coveragesFeatureCollection.features.filter(feature => {
      const hasCoverageArea = Boolean(feature.geometry)
      const kindLabel = getCoverageKindLabel(feature.properties.kind)
      const searchableText = [
        feature.properties.name,
        feature.properties.key,
        kindLabel,
        hasCoverageArea ? 'Área definida' : 'Sem área',
      ]
        .filter(Boolean)
        .join(' ')

      return normalizeTextForSearch(searchableText).includes(normalizedItemSearchText)
    })
  }, [coveragesFeatureCollection, normalizedItemSearchText])
  const filteredCoverageFeatureCount = filteredCoverageFeatures.length
  const selectedItemStatus = hasPendingPolygonEdits
    ? 'Edição de polígono pendente'
    : hasUnsavedCoverageChanges
      ? 'Alterações não salvas'
      : selectedItem
        ? 'Pronto para editar'
        : 'Selecione um item na lista'

  const toolHelpTopics = useMemo<ToolHelpTopic[]>(() => {
    const itemPluralName = currentTool?.unitPlural?.toLocaleLowerCase('pt-BR') || 'itens'

    return [
      {
        id: 'edit-polygon',
        title: 'Editar polígono',
        description: `Selecione um dos ${itemPluralName}, clique no mapa para adicionar pontos e arraste os pontos para ajustar a área.`,
        icon: faPenToSquare,
      },
      {
        id: 'undo',
        title: 'Desfazer',
        description: 'Volta para o estado anterior da edição de polígonos.',
        shortcut: 'Ctrl + Z',
        icon: faArrowRotateLeft,
      },
      {
        id: 'redo',
        title: 'Refazer',
        description: 'Restaura a última alteração desfeita na edição.',
        shortcut: 'Ctrl + Y ou Ctrl + Shift + Z',
        icon: faArrowRotateRight,
      },
      {
        id: 'resolve',
        title: 'Resolver polígonos',
        description:
          'Aplica o polígono atual e ajusta sobreposições com outros polígonos quando a topologia está ativa.',
        shortcut: 'Ctrl + Enter',
        icon: faPenToSquare,
      },
      {
        id: 'smart-mapping',
        title: 'Mapeamento inteligente',
        description: 'Gera polígonos automaticamente a partir das opções configuradas para a tela.',
        shortcut: 'Ctrl + M',
        icon: faWandMagicSparkles,
      },
      {
        id: 'remove-fragments',
        title: 'Remover fragmentos',
        description:
          'Mantém apenas a maior área de cada multipolígono para reduzir fragmentações indesejadas.',
        shortcut: 'Ctrl + Shift + R',
        icon: faObjectUngroup,
      },
      {
        id: 'fill-delimitation',
        title: 'Preencher toda a área',
        description:
          'Preenche o item selecionado com a área livre da delimitação, descontando áreas já ocupadas.',
        shortcut: 'Ctrl + Shift + F',
        icon: faFillDrip,
      },
      {
        id: 'delete-selected',
        title: 'Apagar polígono selecionado',
        description: 'Remove apenas o polígono do item selecionado.',
        shortcut: 'Ctrl + Delete',
        icon: faTrash,
      },
      {
        id: 'clear-all',
        title: 'Limpar todos os polígonos',
        description: 'Remove todos os polígonos carregados na tela após confirmação.',
        shortcut: 'Ctrl + Shift + L',
        icon: faTrash,
      },
      {
        id: 'remove-vertex',
        title: 'Remover vértice',
        description: 'Clique em um ponto do polígono em edição para removê-lo.',
        icon: faTrash,
      },
      {
        id: 'save',
        title: 'Salvar',
        description: 'Envia as alterações pendentes para o servidor.',
        shortcut: 'Ctrl + S',
        icon: faFloppyDisk,
      },
      {
        id: 'help',
        title: 'Ajuda',
        description: 'Abre este guia com atalhos e explicações das ferramentas.',
        shortcut: 'Ctrl + /',
        icon: faCircleQuestion,
      },
    ]
  }, [currentTool?.unitPlural])

  const hectareArea = useMemo(
    () =>
      `${(polygonEditor.polygon ? area(polygonEditor.polygon) / 10000 : 0).toLocaleString('pt-BR', {
        maximumFractionDigits: 2,
      })} ha`,
    [polygonEditor.polygon],
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const withModifier = event.ctrlKey || event.metaKey
      const key = event.key.toLowerCase()

      if (withModifier && key === '/') {
        event.preventDefault()
        setIsHelpModalOpen(true)
        return
      }

      const hasOpenModal =
        isHelpModalOpen ||
        isSmartMappingOpen ||
        Boolean(pendingBaseLayersConfirmation) ||
        isClearAllPolygonsConfirmationOpen

      if (isLoadingFeature || hasOpenModal) {
        return
      }

      if (isEditableTarget(event.target)) {
        return
      }

      const isUndoKey = key === 'z' && !event.shiftKey
      const isRedoKey = key === 'y' || (key === 'z' && event.shiftKey)
      const isResolveKey = key === 'enter'
      const isSmartMappingKey = key === 'm' && !event.shiftKey
      const isRemoveFragmentsKey = key === 'r' && event.shiftKey
      const isClipByDelimitationKey = key === 'x' && event.shiftKey
      const isFillEntireDelimitationKey = key === 'f' && event.shiftKey
      const isClearAllPolygonsKey = key === 'l' && event.shiftKey
      const isDeleteSelectedPolygonKey = key === 'delete' && !event.shiftKey
      const isSaveKey = key === 's' && !event.shiftKey

      if (!withModifier) {
        return
      }

      if (isUndoKey && undoStackRef.current.length > 0) {
        event.preventDefault()
        handleUndo()
        return
      }
      if (isRedoKey && redoStackRef.current.length > 0) {
        event.preventDefault()
        handleRedo()
        return
      }
      if (isResolveKey && selectedItem && polygonEditor.polygon && !smartMapping.isLoading) {
        event.preventDefault()
        handleResolvePolygons()
        return
      }
      if (isSmartMappingKey && !save.isLoading && !smartMappingBlockReason) {
        event.preventDefault()
        setIsSmartMappingOpen(true)
        return
      }
      if (
        isRemoveFragmentsKey &&
        !delimitationHasMultiplePolygons &&
        Boolean(coveragesFeatureCollection)
      ) {
        event.preventDefault()
        handleRemoveFragments()
        return
      }
      if (
        isFillEntireDelimitationKey &&
        selectedItem &&
        delimitation?.shape &&
        coveragesFeatureCollection
      ) {
        event.preventDefault()
        handleFillEntireDelimitation()
        return
      }
      if (isClearAllPolygonsKey && coveragesFeatureCollection && hasAnyPolygon) {
        event.preventDefault()
        handleClearAllPolygons()
        return
      }
      if (isDeleteSelectedPolygonKey && selectedItem && hasSelectedPolygon) {
        event.preventDefault()
        handleDeleteSelectedPolygon()
        return
      }
      if (isSaveKey && !save.isLoading) {
        event.preventDefault()
        void handleSave()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [
    coveragesFeatureCollection,
    delimitation?.shape,
    delimitationHasMultiplePolygons,
    handleClearAllPolygons,
    handleDeleteSelectedPolygon,
    handleFillEntireDelimitation,
    handleRedo,
    handleRemoveFragments,
    handleResolvePolygons,
    handleSave,
    handleUndo,
    hasAnyPolygon,
    hasSelectedPolygon,
    isClearAllPolygonsConfirmationOpen,
    isHelpModalOpen,
    isLoadingFeature,
    isSmartMappingOpen,
    pendingBaseLayersConfirmation,
    polygonEditor.polygon,
    save.isLoading,
    selectedItem,
    smartMapping.isLoading,
    smartMappingBlockReason,
  ])

  return (
    <ScreenWrapper
      isLoadingFeature={isLoadingFeature}
      isLoadingScreen={isLoading}
      error={currentTool?.error}
    >
      {currentMapPosition ? (
        <>
          <GoogleMap
            mapType='hybrid'
            isImmediately
            {...currentMapPosition}
            onClick={handlePolygonEditorMapClick}
          >
            {delimitationFeatureCollection ? (
              <GeoJson
                geoJson={delimitationFeatureCollection}
                dataMapStyle={{
                  zIndex: -1,
                  // Allow drawing clicks to reach the map layer.
                  clickable: false,
                  fillOpacity: 0,
                  strokeColor: theme.colors.danger,
                  strokeOpacity: 0.8,
                  strokeWeight: 3,
                }}
              />
            ) : null}
            {filteredCoveragesFeatureCollection ? (
              <GeoJson
                geoJson={filteredCoveragesFeatureCollection}
                onClick={handlePolygonClick}
                dataMapStyle={
                  !getCustomProperty
                    ? {
                        zIndex: 0,
                        fillColor: theme.colors.tertiary,
                        strokeColor: theme.colors.tertiary,
                      }
                    : undefined
                }
              />
            ) : null}
            {selectedItem ? (
              <PolygonEditor
                {...polygonEditor}
                onMapClick={handlePolygonEditorMapClick}
                onMarkerDragEnd={handlePolygonEditorMarkerDragEnd}
                onMarkerClick={handlePolygonEditorMarkerClick}
                onGhostMarkerDragEnd={handlePolygonEditorGhostMarkerDragEnd}
              />
            ) : null}
            {points}
          </GoogleMap>
        </>
      ) : null}
      {!isCompatibleDelimitation ? (
        <OverlayLoading>
          <div className='center'>
            <div className='message'>{incompatibleDelimitationMessage}</div>
          </div>
        </OverlayLoading>
      ) : null}
      {isCompatibleDelimitation && coveragesFeatureCollection ? (
        <>
          <FloatingToolbar>
            <ToolbarButtonGroup>
              <ToolbarTooltipTarget
                data-tooltip-id={toolbarTooltipId}
                data-tooltip-content='Desfazer'
              >
                <ToolbarIconButton
                  icon={faArrowRotateLeft}
                  disabled={!canUndo || isLoadingFeature}
                  onClick={handleUndo}
                  size='sm'
                  backgroundColor={theme.colors.primary}
                  color={theme.colors.lightText}
                  aria-label='Desfazer'
                />
              </ToolbarTooltipTarget>
              <ToolbarTooltipTarget
                data-tooltip-id={toolbarTooltipId}
                data-tooltip-content='Refazer'
              >
                <ToolbarIconButton
                  icon={faArrowRotateRight}
                  disabled={!canRedo || isLoadingFeature}
                  onClick={handleRedo}
                  size='sm'
                  backgroundColor={theme.colors.primary}
                  color={theme.colors.lightText}
                  aria-label='Refazer'
                />
              </ToolbarTooltipTarget>
            </ToolbarButtonGroup>

            <ToolbarButtonGroup>
              <ContextMenuButton
                xBasis='left'
                yBasis='top'
                xBasisOffset={50}
                functionalities={[
                  {
                    title: 'Mapeamento inteligente',
                    run: () => setIsSmartMappingOpen(true),
                    isDisabled: save.isLoading || Boolean(smartMappingBlockReason),
                  },
                  {
                    title: 'Remover fragmentos',
                    run: handleRemoveFragments,
                    isDisabled: delimitationHasMultiplePolygons || !coveragesFeatureCollection,
                  },
                  {
                    title: 'Preencher toda a área',
                    run: handleFillEntireDelimitation,
                    isDisabled:
                      !selectedItem || !delimitation?.shape || !coveragesFeatureCollection,
                  },
                ]}
                button={
                  <ToolbarTooltipTarget
                    data-tooltip-id={toolbarTooltipId}
                    data-tooltip-content={'Mais ferramentas'}
                  >
                    <ToolbarIconButton
                      icon={faPlus}
                      size='sm'
                      backgroundColor={theme.colors.primary}
                      color={theme.colors.lightText}
                      aria-label='Mais ferramentas'
                    />
                  </ToolbarTooltipTarget>
                }
              />
            </ToolbarButtonGroup>

            <ToolbarButtonGroup>
              <ContextMenuButton
                xBasis='left'
                yBasis='top'
                xBasisOffset={50}
                functionalities={[
                  {
                    title: 'Apagar polígono selecionado',
                    run: handleDeleteSelectedPolygon,
                    isDisabled: !selectedItem || !hasSelectedPolygon,
                  },
                  {
                    title: 'Limpar todos os polígonos',
                    run: handleClearAllPolygons,
                    isDisabled: !coveragesFeatureCollection || !hasAnyPolygon,
                  },
                ]}
                button={
                  <ToolbarTooltipTarget
                    data-tooltip-id={toolbarTooltipId}
                    data-tooltip-content={'Remover'}
                  >
                    <ToolbarIconButton
                      icon={faTrash}
                      size='sm'
                      backgroundColor={theme.colors.danger}
                      color={theme.colors.lightText}
                      aria-label='Remover'
                      className='danger-button'
                    />
                  </ToolbarTooltipTarget>
                }
              />
            </ToolbarButtonGroup>

            <ToolbarButtonGroup>
              <ToolbarTooltipTarget data-tooltip-id={toolbarTooltipId} data-tooltip-content='Ajuda'>
                <ToolbarIconButton
                  icon={faCircleQuestion}
                  onClick={() => setIsHelpModalOpen(true)}
                  size='sm'
                  backgroundColor={theme.colors.primary}
                  color={theme.colors.lightText}
                  aria-label='Ajuda'
                />
              </ToolbarTooltipTarget>
            </ToolbarButtonGroup>
            <Tooltip id={toolbarTooltipId} place='bottom' style={{ zIndex: 20 }} />
          </FloatingToolbar>

          <StatusPanel>
            <StatusTitle>
              {selectedItem ? selectedItem.name : 'Nenhum item selecionado'}
            </StatusTitle>
            <StatusSubtitle>
              {selectedItem
                ? selectedItemStatus
                : `${mappedCoverageCount} de ${totalCoverageCount} com área definida`}
            </StatusSubtitle>
            {selectedItem ? (
              <StatusSubtitle>
                {`${polygonEditor.points.length} ponto${polygonEditor.points.length <= 1 ? '' : 's'} | ${hectareArea}`}
              </StatusSubtitle>
            ) : null}
            <StatusBadgeRow>
              <StatusBadge>{totalCoverageCount} registros</StatusBadge>
              <StatusBadge $isActive={Boolean(selectedItem)}>
                {selectedItem ? 'Editando' : 'Mapa'}
              </StatusBadge>
            </StatusBadgeRow>
          </StatusPanel>
        </>
      ) : null}
      <MainMenuControls
        layers={layers}
        baseLayers={baseLayers}
        setBaseLayers={setBaseLayersWithUnsavedConfirmation}
        variables={variables}
        setMapType={() => {}}
        mapType={currentTool?.mapType}
        setMap3DOption={() => {}}
        map3DOption={currentTool?.map3DOption}
        isHighResolutionSpace={true}
        editProps={{
          currentTool,
        }}
        bottom={70}
      >
        {isCompatibleDelimitation && coveragesFeatureCollection ? (
          <ListInformationWrapper icon={faList} top={!selectedItem ? 147 : 165} bottom={0}>
            <Aside>
              <div className='header'>
                <div className='header-text'>
                  <div className='header-title'>
                    {currentTool?.unitPlural ? currentTool.unitPlural : 'Itens'}
                  </div>
                  <div className='header-subtitle'>
                    {delimitation?.name ? delimitation.name : 'Selecione um município'}
                  </div>

                  <div className='search-input-wrapper'>
                    <FontAwesomeIcon className='search-icon' icon={faMagnifyingGlass} />
                    <input
                      type='text'
                      className='search-input'
                      value={itemSearchText}
                      onChange={event => setItemSearchText(event.currentTarget.value)}
                      placeholder={`Buscar ${currentTool?.unitPlural?.toLocaleLowerCase('pt-BR') || 'itens'}`}
                      aria-label='Buscar itens'
                    />
                    {itemSearchText ? (
                      <button
                        type='button'
                        className='search-clear-button'
                        onClick={() => setItemSearchText('')}
                        aria-label='Limpar busca de itens'
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    ) : null}
                  </div>

                  {normalizedItemSearchText ? (
                    <div className='search-count'>
                      {filteredCoverageFeatureCount} de {totalCoverageCount} encontrados
                    </div>
                  ) : null}
                </div>
              </div>

              <div className='items'>
                {filteredCoverageFeatureCount > 0 ? (
                  <ul>
                    {filteredCoverageFeatures.map(item => {
                      const hasCoverageArea = Boolean(item.geometry)
                      const kindLabel = getCoverageKindLabel(item.properties.kind)
                      const subtitle = kindLabel
                        ? `${hasCoverageArea ? 'Área definida' : 'Sem área'} • ${kindLabel}`
                        : hasCoverageArea
                          ? 'Área definida'
                          : 'Sem área'

                      return (
                        <li
                          key={item.properties.key}
                          id={`li-${item.properties.key}`}
                          className={selectedItem?.key === item.properties.key ? 'is-active' : ''}
                        >
                          <button
                            type='button'
                            className='item-card'
                            onClick={() => {
                              const alreadySelected =
                                selectedItemRef.current?.key === item.properties.key

                              if (!alreadySelected && hasPendingPolygonEditsRef.current) {
                                pushUndoSnapshot()
                                applyPolygon()
                                setHasPendingPolygonEdits(false)
                              }

                              const selectedItemNext = {
                                ...item.properties,
                                coverageArea:
                                  alreadySelected &&
                                  hasPendingPolygonEditsRef.current &&
                                  polygonEditor.polygon
                                    ? polygonEditor.polygon.geometry
                                    : (item.geometry as Polygon | MultiPolygon | null),
                              } as Item

                              if (!alreadySelected) {
                                setSelectedItem(selectedItemNext)
                              }

                              const navigationPosition = mapNavigationForItem(selectedItemNext)
                              if (navigationPosition) {
                                setMapNavigationPosition(navigationPosition)
                              }
                            }}
                            aria-current={
                              selectedItem?.key === item.properties.key ? 'true' : undefined
                            }
                          >
                            <span className='item-dot' data-mapped={hasCoverageArea} />
                            <span className='item-text'>
                              <span className='item-header'>
                                <span className='item-title'>{item.properties.name}</span>
                                {renderItemTag && renderItemTag(item.properties as Item)}
                              </span>
                              <span className='item-subtitle'>{subtitle}</span>
                            </span>
                          </button>
                          {selectedItem?.key === item.properties.key && renderItemContent
                            ? renderItemContent(
                                {
                                  ...item.properties,
                                  coverageArea: item.geometry,
                                } as Item,
                                handleChangeItem,
                              )
                            : null}
                        </li>
                      )
                    })}
                  </ul>
                ) : (
                  <div className='empty-list'>Nenhum item encontrado</div>
                )}
              </div>
            </Aside>
          </ListInformationWrapper>
        ) : null}
      </MainMenuControls>
      {isSmartMappingOpen ? (
        <Modal
          isOpen={isSmartMappingOpen}
          onClose={() => setIsSmartMappingOpen(false)}
          baseWidth='auto'
          styles={{
            overlay: {
              zIndex: 10,
            },
          }}
        >
          <WrapperSmartMapping>
            <label>
              Quantil de outliers em cada classificação <br />
              <input
                type='number'
                value={smartMappingOptions.classificationOutliersQuantile}
                onChange={event =>
                  setSmartMappingOptions({
                    ...smartMappingOptions,
                    classificationOutliersQuantile: Number(event.target.value),
                  })
                }
                required
                min={0}
                step={0.01}
                max={0.3}
              />
            </label>
            <label>
              Quantil de outliers gerais <br />
              <input
                type='number'
                value={smartMappingOptions.generalOutliersQuantile}
                onChange={event =>
                  setSmartMappingOptions({
                    ...smartMappingOptions,
                    generalOutliersQuantile: Number(event.target.value),
                  })
                }
                required
                min={0}
                step={0.01}
                max={0.3}
              />
            </label>

            <label>
              Ocorrências mínimas por classificação <br />
              <input
                type='number'
                value={
                  smartMappingOptions.minOccurrencesPerClassification === 'auto'
                    ? ''
                    : smartMappingOptions.minOccurrencesPerClassification
                }
                onChange={event => {
                  const valueAsNumber = event.currentTarget.valueAsNumber
                  const valueText = event.currentTarget.value.trim()

                  setSmartMappingOptions({
                    ...smartMappingOptions,
                    minOccurrencesPerClassification:
                      valueText === '' || !Number.isFinite(valueAsNumber) ? 'auto' : valueAsNumber,
                  })
                }}
                placeholder='Automático'
                min={1}
                step={1}
              />
            </label>

            <label>
              <input
                type='checkbox'
                checked={smartMappingOptions.useCustomDelimitationPolygon}
                onChange={event =>
                  setSmartMappingOptions({
                    ...smartMappingOptions,
                    useCustomDelimitationPolygon: event.target.checked,
                  })
                }
              />{' '}
              Cortar pela delimitação geográfica
            </label>
            {(currentTool?.key === 'healthUnits' ||
              currentTool?.key === 'healthTeams' ||
              currentTool?.key === 'healthProfessionals') &&
            smartMappingOptions.useCustomDelimitationPolygon ? (
              <label>
                <input
                  type='checkbox'
                  checked={smartMappingOptions.usePointsDelimitationPolygon ?? true}
                  onChange={event =>
                    setSmartMappingOptions({
                      ...smartMappingOptions,
                      usePointsDelimitationPolygon: event.target.checked,
                    })
                  }
                />{' '}
                Limitar pelo contorno dos pontos
              </label>
            ) : null}
            {currentTool?.key === 'healthUnits' ||
            currentTool?.key === 'healthTeams' ||
            currentTool?.key === 'healthProfessionals' ? (
              <label>
                <input
                  type='checkbox'
                  checked={Boolean(smartMappingOptions.respectCentralPoint)}
                  onChange={event =>
                    setSmartMappingOptions({
                      ...smartMappingOptions,
                      respectCentralPoint: event.target.checked,
                    })
                  }
                />{' '}
                Respeitar o ponto central
              </label>
            ) : null}
            <label>
              <input
                type='checkbox'
                checked={smartMappingOptions.withTopology}
                onChange={event =>
                  setSmartMappingOptions({
                    ...smartMappingOptions,
                    withTopology: event.target.checked,
                  })
                }
                disabled
              />{' '}
              Topologia
            </label>
            <ButtonVariants
              label='Atualizar polígonos'
              onClick={handleUpdatePolygonsUsingSmartMapping}
              disabled={smartMapping.isLoading}
              size='sm'
              variant='primary'
            />
          </WrapperSmartMapping>
        </Modal>
      ) : null}

      {isHelpModalOpen ? (
        <Modal
          isOpen={isHelpModalOpen}
          onClose={() => setIsHelpModalOpen(false)}
          baseWidth='720px'
          styles={{
            overlay: {
              zIndex: 10,
            },
          }}
        >
          <HelpModalContent>
            <HelpModalHeader>
              <HelpModalTitle>Atalhos e ferramentas</HelpModalTitle>
              <HelpModalSubtitle>
                Consulte as ações disponíveis para edição de polígonos e seus atalhos de teclado.
              </HelpModalSubtitle>
            </HelpModalHeader>

            <HelpTopicGrid>
              {toolHelpTopics.map(topic => (
                <HelpTopicCard key={topic.id}>
                  <HelpTopicHeader>
                    <HelpTopicIcon>
                      <FontAwesomeIcon icon={topic.icon} />
                    </HelpTopicIcon>
                    <HelpTopicTitle>{topic.title}</HelpTopicTitle>
                  </HelpTopicHeader>
                  <HelpTopicDescription>{topic.description}</HelpTopicDescription>
                  {topic.shortcut ? <HelpShortcut>{topic.shortcut}</HelpShortcut> : null}
                </HelpTopicCard>
              ))}
            </HelpTopicGrid>
          </HelpModalContent>
        </Modal>
      ) : null}

      {pendingBaseLayersConfirmation ? (
        <ConfirmModal
          isOpen={Boolean(pendingBaseLayersConfirmation)}
          onClose={() => setPendingBaseLayersConfirmation(null)}
          onConfirm={handleConfirmBaseLayersChange}
          title='Descartar alterações?'
          description='Existem alterações não salvas. Deseja trocar a delimitação e descartar essas alterações?'
        />
      ) : null}

      {isClearAllPolygonsConfirmationOpen ? (
        <ConfirmModal
          isOpen={isClearAllPolygonsConfirmationOpen}
          onClose={() => setIsClearAllPolygonsConfirmationOpen(false)}
          onConfirm={clearAllPolygons}
          title='Limpar todos os polígonos?'
          description='Isso irá remover os polígonos de todos os itens. Deseja continuar?'
        />
      ) : null}

      {isCompatibleDelimitation && coveragesFeatureCollection && (
        <BottomBar>
          <ButtonVariants
            icon={faPenToSquare}
            onClick={handleResolvePolygons}
            disabled={!selectedItem || !polygonEditor.polygon || smartMapping.isLoading}
            size='sm'
            variant='secondary'
            label='Resolver polígonos'
          />
          <ButtonVariants
            icon={faFloppyDisk}
            onClick={handleSave}
            disabled={save.isLoading}
            isLoading={save.isLoading}
            variant='primary'
            label='Salvar'
          />
        </BottomBar>
      )}
    </ScreenWrapper>
  )
}
